/**
 * WorkflowPanelProvider
 *
 * Owns:
 * - React Query for GET /workflows (staleTime 30s, refetchOnWindowFocus: true)
 * - A modal registry — keyed by modalKey, renders via createPortal to avoid
 *   the `inert` ancestor issue (learnings.md: dialogs inside inert parents fail)
 * - Context: { workflows, isLoading, dismiss, restore, openModal, closeModal }
 */

import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listWorkflows, dismissWorkflow, restoreWorkflow } from '../../lib/workflow.api';
import { onMutation } from '../../lib/api.core';
import type { WorkflowListItem } from '../../types/workflow';
import { WorkflowPanelContext, type WorkflowPanelContextValue } from './WorkflowPanelContext';
import { ReviewTenantsModalPanel } from './modals/ReviewTenantsModalPanel';
import { BYOvsWizardModalPanel } from './modals/BYOvsWizardModalPanel';
import { ChooseApplicationModalPanel } from './modals/ChooseApplicationModalPanel';

// ── Modal registry ──────────────────────────────────────────────────────────

type ModalComponentProps = {
  params: Record<string, string>;
  onClose: () => void;
};

const MODAL_REGISTRY: Record<string, React.ComponentType<ModalComponentProps>> = {
  'review-tenants': ReviewTenantsModalPanel,
  'byo-vs-wizard': BYOvsWizardModalPanel,
  'choose-application': ChooseApplicationModalPanel,
};

// ── Context ─────────────────────────────────────────────────────────────────
// Context interface and creation moved to WorkflowPanelContext.ts to satisfy
// react-refresh/only-export-components rule

// ── Provider ────────────────────────────────────────────────────────────────

interface ActiveModal {
  key: string;
  params: Record<string, string>;
}

export function WorkflowPanelProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);
  const [closeSignal, setCloseSignal] = useState(0);
  const [isRailExpanded, setIsRailExpanded] = useState(
    () => localStorage.getItem('workflow-rail-expanded') === 'true',
  );
  const toggleRail = useCallback(() => setIsRailExpanded((v) => !v), []);

  useEffect(() => {
    localStorage.setItem('workflow-rail-expanded', String(isRailExpanded));
  }, [isRailExpanded]);

  const closePanel = useCallback(() => {
    setCloseSignal((n) => n + 1);
  }, []);

  useEffect(() => {
    return onMutation(() => {
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    });
  }, [queryClient]);

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['workflows'],
    queryFn: listWorkflows,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
    throwOnError: false,
  });

  const workflows: WorkflowListItem[] = data?.workflows ?? [];

  const dismiss = useCallback(async (id: string) => {
    await dismissWorkflow(id);
    await queryClient.invalidateQueries({ queryKey: ['workflows'] });
  }, [queryClient]);

  // Latent capability — intentionally not surfaced in the V1 manager UX (ADR-JO-012).
  // Dismiss is sticky and has no restore affordance by product decision; the backend
  // restore endpoint is retained (and integration-tested) for a possible future use.
  const restore = useCallback(async (id: string) => {
    await restoreWorkflow(id);
    await queryClient.invalidateQueries({ queryKey: ['workflows'] });
  }, [queryClient]);

  const openModal = useCallback((key: string, params: Record<string, string>) => {
    setActiveModal({ key, params });
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal(null);
    // Refetch workflows after any modal action (tenant added, lease created, etc.)
    void queryClient.invalidateQueries({ queryKey: ['workflows'] });
  }, [queryClient]);

  const value: WorkflowPanelContextValue = {
    workflows,
    isLoading,
    isError,
    isFetching,
    refetch: () => void refetch(),
    dismiss,
    restore,
    openModal,
    closeModal,
    closeSignal,
    closePanel,
    isRailExpanded,
    toggleRail,
  };

  // Render active panel-hosted modal via createPortal to avoid `inert` ancestor issue
  const ModalComponent = activeModal ? MODAL_REGISTRY[activeModal.key] : null;

  return (
    <WorkflowPanelContext.Provider value={value}>
      {children}
      {ModalComponent && activeModal &&
        createPortal(
          <ModalComponent params={activeModal.params} onClose={closeModal} />,
          document.body
        )
      }
    </WorkflowPanelContext.Provider>
  );
}
